import type { AgentStatusTone } from "./agents-utils"
import type { M365ConnectionStatus } from "@/api/client/m365-connection"

import { m365ConnectionTone } from "./agents-utils"

/**
 * What the workspace's Microsoft 365 pill shows.
 *
 * - `loading`: no answer yet.
 * - `service_unavailable`: the backend answered 503 — the M365 connection
 *   service itself is down on this deployment (e.g. its cache key failed to
 *   load), so no connection can work until an administrator fixes it. This
 *   gets a visible attention state; before #3207 it looked identical to
 *   "still checking".
 * - `unknown`: any other failure, or an answer without a status. Never
 *   claims a connection it has not read back.
 * - `status`: a real connection status read back from the backend.
 */
export type M365PillState =
  | { kind: "loading"; tone: AgentStatusTone }
  | { kind: "service_unavailable"; tone: AgentStatusTone }
  | { kind: "unknown"; tone: AgentStatusTone }
  | { kind: "status"; tone: AgentStatusTone; status: M365ConnectionStatus["status"] }

/** True when the connection read failed because the M365 service is unavailable (HTTP 503). */
export function isM365ServiceUnavailable(error: unknown): boolean {
  const failure = error as { status?: unknown } | null | undefined
  return failure?.status === 503
}

export function m365PillState(query: {
  data?: Partial<M365ConnectionStatus> | null
  isError?: boolean
  error?: unknown
}): M365PillState {
  if (query.isError) {
    return isM365ServiceUnavailable(query.error)
      ? { kind: "service_unavailable", tone: "attention" }
      : { kind: "unknown", tone: "neutral" }
  }
  if (!query.data) return { kind: "loading", tone: "neutral" }
  const status = query.data.status
  if (typeof status !== "string") return { kind: "unknown", tone: "neutral" }
  return { kind: "status", tone: m365ConnectionTone(status), status }
}
